import { Box, Tab, Tabs } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import {
  ApplicationCycleDataGridRow,
  ApplicationDeadlineAdminProps,
} from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { ApplicationTypes, applicationTypeAsString } from '@/models/ApplicationType';
import { GrowthAwardProgramIds, programIdToProgramName } from '@/models/Program';
import { StateProps } from '@/models/State';
import { logError } from '@/utils/errorHandlerUtil';

import Admin_ApplicationCycleButtons from './ApplicationCycleButtons';
import AdminSectionHeader from '../AdminSectionHeader';
import { AccordionWrapper } from './AccordionWrapper';
import AdminApplicationDeadlineDataGrid from './AdminApplicationDeadlineDataGrid';
import { useApplicationDeadline } from '@/hooks/useApplicationDeadline';

const APP_CYCLE_TABS = ['Manage Cycles', 'Deadlines'] as const;
type AppCycleTab = (typeof APP_CYCLE_TABS)[number];

const Admin_ManageApplicationCycles: React.FC = () => {
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { allStates } = useContext(ConfigContext);
  const { translatedText } = useContext(LocaleContext);
  const { getApplicationDeadlineDataGrid } = useApplicationDeadline();
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [activeTab, setActiveTab] = useState<AppCycleTab>(APP_CYCLE_TABS[0]);
  const [deadlineRows, setDeadlineRows] = useState<ApplicationDeadlineAdminProps[]>([]);
  const [deadlinesLoading, setDeadlinesLoading] = useState(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const mapToApplicationCycleDataGridRow = useCallback(
    (currentAppCycles: ApplicationCycle[]) => {
      return currentAppCycles.map((applicationCycle: ApplicationCycle) => {
        let programName = programIdToProgramName[applicationCycle.programId];

        if (
          GrowthAwardProgramIds.includes(applicationCycle.programId) &&
          applicationCycle.applicationTypeId !== ApplicationTypes.Standard
        ) {
          programName = `${programName} - ${applicationTypeAsString(
            applicationCycle.applicationTypeId
          )}`;
        }

        return {
          id: applicationCycle.id,
          program: programName,
          stateName:
            allStates.find((state: StateProps) => applicationCycle.stateId === state.id)?.abbrev ??
            null,
          applicationTemplateName: null,
          applicationCycleName: applicationCycle.applicationCycleName,
          applicationCycleYear: applicationCycle.year,
          openDate: applicationCycle.openDate,
          closeDate: applicationCycle.closeDate,
        } satisfies ApplicationCycleDataGridRow;
      });
    },
    [allStates]
  );

  const futureApplicationCycles = useMemo(() => {
    return applicationCycles
      .filter((ac) => dayjs(ac.openDate) >= dayjs() && dayjs(ac.closeDate) >= dayjs())
      .map((ac) => mapToApplicationCycleDataGridRow([ac])[0]);
  }, [applicationCycles, mapToApplicationCycleDataGridRow]);

  const pastApplicationCycles = useMemo(() => {
    return applicationCycles
      .filter((ac) => dayjs(ac.openDate) <= dayjs() && dayjs(ac.closeDate) <= dayjs())
      .map((ac) => mapToApplicationCycleDataGridRow([ac])[0]);
  }, [applicationCycles, mapToApplicationCycleDataGridRow]);

  const currentApplicationCycles = useMemo(() => {
    return applicationCycles
      .filter((ac) => dayjs(ac.openDate) <= dayjs() && dayjs(ac.closeDate) >= dayjs())
      .map((ac) => mapToApplicationCycleDataGridRow([ac])[0]);
  }, [applicationCycles, mapToApplicationCycleDataGridRow]);

  useEffect(() => {
    if (activeTab !== 'Deadlines') return;

    let cancelled = false;

    const loadDeadlineRows = async () => {
      setDeadlinesLoading(true);

      try {
        const deadlines = (await getApplicationDeadlineDataGrid()).map((deadline, index) => ({
          ...deadline,
          rowKey: `${deadline.applicationCycle.id}-${deadline.stateId}-${index}`,
        }));
        if (!cancelled) {
          setDeadlineRows(deadlines);
        }
      } catch (error) {
        logError('Error loading application deadline rows for admin grid.', error);
      } finally {
        if (!cancelled) {
          setDeadlinesLoading(false);
        }
      }
    };

    void loadDeadlineRows();

    return () => {
      cancelled = true;
    };
  }, [activeTab, allStates, applicationCycles, getApplicationDeadlineDataGrid]);

  return (
    <Box sx={{ width: '100%' }}>
      <AdminSectionHeader
        title={translatedText.admin.applicationCyclesHeader}
        actions={<Admin_ApplicationCycleButtons />}
      />
      <Tabs
        value={activeTab}
        onChange={(_, value: AppCycleTab) => setActiveTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
      >
        {APP_CYCLE_TABS.map((tab) => (
          <Tab key={tab} label={tab} value={tab} />
        ))}
      </Tabs>

      <Box sx={{ p: 2 }}>
        {activeTab === 'Manage Cycles' && (
          <>
            <AccordionWrapper
              index={0}
              title={translatedText.admin.applicationCyclesCurrent}
              canEdit={true}
              rows={currentApplicationCycles}
              expanded={expanded}
              handleChange={handleChange}
            />
            <AccordionWrapper
              index={1}
              title={translatedText.admin.applicationCyclesUpcoming}
              canEdit={true}
              rows={futureApplicationCycles}
              expanded={expanded}
              handleChange={handleChange}
            />
            <AccordionWrapper
              index={2}
              title={translatedText.admin.applicationCyclesPast}
              canEdit={true}
              rows={pastApplicationCycles}
              expanded={expanded}
              handleChange={handleChange}
            />
          </>
        )}

        {activeTab === 'Deadlines' && (
          <AdminApplicationDeadlineDataGrid rows={deadlineRows} loading={deadlinesLoading} />
        )}
      </Box>
    </Box>
  );
};

export default Admin_ManageApplicationCycles;
